import { Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { GridValidRowModel } from "@mui/x-data-grid-premium";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { DataGrid, DataGridProps } from "components/DataGrid";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
  wasteGeneratorStatusToLongLabel,
} from "encamp-shared/src/waste";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";
import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import pluralize from "pluralize";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintMonthYear } from "util/dates";

import { ExpandableInfoContainerCombined } from "./ExpandableInfoSection";
import { useGeneratorStatusSummary } from "./useGeneratorStatusSummary";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { useMonthlyGeneration } from "./useMonthlyGeneration";
import { useWasteFacility } from "./useWasteFacility";
import { useWasteFacilityState } from "./WasteFacilityContext";

const updateNotificationTooltip =
  "If the facility's calculated generator statuses differ from the EPA current status, you may need to file an update notification with the EPA.";

const currentStatusTooltip =
  "Federal waste generator status as currently listed with the EPA, for both federal and state, if applicable.\
  \n\nReported data can take up to 2 weeks to update.\n\n" + updateNotificationTooltip;

const epaStatusChangesTooltip =
  "The generator statuses listed with the EPA, if they change month to month.";

const calculatedStatusesTooltip =
  "Statuses are calculated from vendor line items, but if no vendor data is present, we will use e‑Manifest data.";

const potentialExceedancesTooltip =
  "Any months where our calculated generator status exceeds the EPA generator status.\n\n" +
  calculatedStatusesTooltip;

const belowThresholdTooltip =
  "Any months where our calculated generator status is below the EPA generator status.\n\n" +
  calculatedStatusesTooltip;

const stateExceedancesTooltip =
  "Months where our calculated STATE generator status exceeds the state status " +
  "reported to the EPA — independent of the federal comparison. Surfaced for states " +
  "whose generator categories differ from the federal rules.\n\n" +
  calculatedStatusesTooltip;

const stateBelowThresholdTooltip =
  "Months where our calculated STATE generator status is below the state status " +
  "reported to the EPA.\n\n" +
  calculatedStatusesTooltip;

const GENERATION_COLUMN_HEADER = "Generation (containers)";

const containerStatusTooltip =
  "Generator status calculated from this facility's container fill events rather than from shipments. " +
  "This is the status that governs the on-site accumulation limit and the central-accumulation clock, so " +
  "when it sits above the EPA status the facility's containers are already being held to the shorter " +
  "deadlines and lower limit of the higher tier.\n\n" +
  updateNotificationTooltip;

/** Generation-driven kg for one month, keyed by month in a lookup. */
type GenerationCell = { hazardousKg: number; acuteKg: number; incompleteFillCount: number };

/**
 * Month key ("YYYY-MM") shared by statusByMonth rows (whose `month` is a
 * first-of-month date string) and monthlyGeneration entries, so the generation
 * column can join to a status row by month.
 */
function monthKey(monthIso: string): string {
  return monthIso.slice(0, 7);
}

/**
 * The generation-column cell text: total federal generation in kg (acute noted
 * separately), or "Incomplete" for a month whose only qualifying ADDs had
 * unknown mass (volume-only), so it doesn't read as a confident 0 kg.
 */
function formatGenerationKg(cell: GenerationCell | undefined): string {
  if (!cell) return "—";
  const totalKg = cell.hazardousKg + cell.acuteKg;
  if (totalKg <= 0) {
    return cell.incompleteFillCount > 0 ? "Incomplete" : "—";
  }
  const base = `${Math.round(totalKg).toLocaleString()} kg`;
  return cell.acuteKg > 0
    ? `${base} (${Math.round(cell.acuteKg).toLocaleString()} kg acute)`
    : base;
}

/**
 * The generator status calculated from container fill events, shown under the
 * EPA status rather than replacing it — both are true and they legitimately
 * differ.
 */
const ContainerDrivenStatusLine = ({
  containerTrackingEnabled,
  containerDrivenStatus,
  reportedFederalStatus,
}: {
  containerTrackingEnabled: boolean;
  containerDrivenStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  reportedFederalStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
}) => {
  const theme = useTheme();
  if (!containerTrackingEnabled) return null;

  return (
    <Stack gap={0.25} sx={{ pt: 0.75 }}>
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography fontSize={11} color="textSecondary">
          Calculated from containers
        </Typography>
        <InfoIconTooltip
          tooltip={containerStatusTooltip}
          iconButtonSx={{ color: theme.palette.grey[400] }}
        />
      </Stack>
      {!containerDrivenStatus && (
        <Typography variant="body2" fontSize={"0.875rem"} color="text.disabled">
          Could not be determined
        </Typography>
      )}
      {containerDrivenStatus && (
        <Stack direction="row" spacing={1}>
          <Typography variant="body2" fontSize={"0.875rem"}>
            {wasteGeneratorStatusToLongLabel(containerDrivenStatus)}
          </Typography>
          <Typography
            fontStyle="italic"
            color="text.disabled"
            variant="caption"
            fontSize={"0.75rem"}
          >
            Federal
          </Typography>
        </Stack>
      )}
      {reportedFederalStatus &&
        containerDrivenStatus &&
        reportedFederalStatus !== containerDrivenStatus && (
          <Typography variant="caption" color="text.secondary" fontSize={"0.75rem"}>
            Above the EPA status — this facility's containers are on the shorter accumulation
            deadlines and lower on-site limit.
          </Typography>
        )}
    </Stack>
  );
};

export const GeneratorStatusTile = () => {
  return (
    <DashboardTile size={{ sm: 12, md: 5 }}>
      <GeneratorStatusTileContent />
    </DashboardTile>
  );
};

const GeneratorStatusTileContent = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data: wasteFacilityData } = useWasteFacility(facilityId);
  const {
    wasteFacilityState: { optimisticFilters, deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const { data: generatorStatusData } = useGeneratorStatusSummary(
    effectiveWasteFacilityId,
    deferredFilters.startDate ? new Date(deferredFilters.startDate) : null,
    deferredFilters.endDate ? new Date(deferredFilters.endDate) : null
  );

  const hasStartAndEndDate = Boolean(
    (optimisticFilters.startDate && optimisticFilters.endDate) ||
    (deferredFilters.startDate && deferredFilters.endDate)
  );

  const {
    currentlyReportedStatus,
    statusChanges,
    exceedances,
    belowThreshold,
    stateExceedances,
    stateBelowThreshold,
  } = generatorStatusData?.generatorStatusSummary ?? {};

  const facility = wasteFacilityData?.facility;
  const facilityState = facility?.state;
  const showState = stateDoesNotFollowFederalGeneratorStatus(facilityState);

  const currentlyReportedStatusArray = useMemo(() => {
    const res = [];
    if (currentlyReportedStatus?.fedWasteGenerator) {
      res.push({
        status: currentlyReportedStatus.fedWasteGenerator.status,
        date: currentlyReportedStatus.fedWasteGenerator.createdDate,
        type: "Federal",
      });
    }
    if (
      currentlyReportedStatus?.stateWasteGenerator &&
      stateDoesNotFollowFederalGeneratorStatus(wasteFacilityData?.facility?.state)
    ) {
      res.push({
        status: currentlyReportedStatus.stateWasteGenerator.status,
        date: currentlyReportedStatus.stateWasteGenerator.createdDate,
        type: `${wasteFacilityData?.facility?.state} State`,
      });
    }
    return res;
  }, [currentlyReportedStatus, wasteFacilityData]);

  // Check if there's any shipment-based data to determine if we should show calculated sections
  const hasShipmentData = useHasWasteShipmentData();

  // Generation-driven additions render only when the tenant is on
  // container tracking. We pull the year from the selected range's end so the
  // generation column can join kg onto each status month. NOTE: a range that
  // spans calendar years only shows generation kg for the end year; the default
  // range is a single year. Flagged for SME/product review.
  const { featureFlags } = useFeatureFlags();
  const containerTrackingEnabled = Boolean(featureFlags[FeatureFlagKey.ContainerTracking]);
  const generationYear = deferredFilters.endDate
    ? new Date(deferredFilters.endDate).getUTCFullYear()
    : deferredFilters.startDate
      ? new Date(deferredFilters.startDate).getUTCFullYear()
      : null;
  const { data: monthlyGenerationData } = useMonthlyGeneration(
    effectiveWasteFacilityId,
    generationYear,
    containerTrackingEnabled
  );
  const generationByMonth = useMemo(() => {
    const map = new Map<string, GenerationCell>();
    for (const entry of monthlyGenerationData?.facility?.monthlyGeneration ?? []) {
      map.set(monthKey(String(entry.month)), {
        hazardousKg: entry.hazardousKg,
        acuteKg: entry.acuteKg,
        incompleteFillCount: entry.incompleteFillCount,
      });
    }
    return map;
  }, [monthlyGenerationData]);
  const hasGenerationData = generationByMonth.size > 0;

  // The calculated sections have historically been gated on shipment data. A
  // pure container-tracking facility has generation rows but no shipments, so
  // without this it would show no calculated sections at all. Render them when
  // there's shipment data OR generation data for a container-tracking tenant.
  // NOTE: flagged for SME review — see PR description.
  const showCalculatedSections = hasShipmentData || (containerTrackingEnabled && hasGenerationData);

  const theme = useTheme();

  const isLoading = !hasStartAndEndDate || !generatorStatusData;

  return (
    <>
      <DashboardTile.Header title="Generator Status" />
      <DashboardTile.ContentArea>
        <Stack spacing={2}>
          {isLoading && (
            <Stack gap={2}>
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
            </Stack>
          )}
          {/* Currently Reported Status - Not expandable */}
          {generatorStatusData && (
            <Stack
              sx={{
                backgroundColor: theme.palette.grey50.main,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                gap: 0.25,
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.25}>
                <Typography fontSize={11} color="textSecondary">
                  {currentlyReportedStatusArray.length > 0
                    ? `EPA Current Status as of ${prettyPrintMonthYear(
                        currentlyReportedStatusArray[0].date
                      )}`
                    : "Facility does not have a generator status listed with the EPA"}
                </Typography>
                <InfoIconTooltip
                  tooltip={currentStatusTooltip}
                  iconButtonSx={{ color: theme.palette.grey[400] }}
                />
              </Stack>
              <Stack gap={0.25}>
                {currentlyReportedStatusArray.map((item) => (
                  <Stack direction="row" spacing={1} key={item.type}>
                    <Typography variant="body2" fontSize={"0.875rem"}>
                      {item.status && wasteGeneratorStatusToLongLabel(item.status)}
                    </Typography>
                    <Typography
                      fontStyle="italic"
                      color="text.disabled"
                      variant="caption"
                      fontSize={"0.75rem"}
                    >
                      {item.type}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <ContainerDrivenStatusLine
                containerTrackingEnabled={containerTrackingEnabled}
                containerDrivenStatus={facility?.generationDrivenStatus}
                reportedFederalStatus={currentlyReportedStatus?.fedWasteGenerator?.status}
              />
            </Stack>
          )}

          {/* Show the calculated sections when there's shipment data, or when a
              container-tracking tenant has generation data (see showCalculatedSections). */}
          {showCalculatedSections && (
            <>
              {/* Reported Status Changes - Expandable */}
              {statusChanges && statusChanges.changes.length > 0 && (
                <ExpandableInfoContainerCombined
                  label="EPA Status Changes"
                  text={`${statusChanges.changeCount} ${pluralize(
                    "change",
                    statusChanges.changeCount
                  )} this period`}
                  disabled={!statusChanges.changes.length}
                  infoTooltip={epaStatusChangesTooltip}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "Federal Status",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "reportedStateGeneratorStatus",
                        headerName: "State Status",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    rows={statusChanges.changes}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {/* Potential Exceedances - Expandable */}
              {exceedances && exceedances?.length > 0 && (
                <ExpandableInfoContainerCombined
                  showWarningChip={!!(exceedances?.length && exceedances.length > 0)}
                  disabled={!exceedances?.length}
                  label="Potential Exceedances"
                  infoTooltip={potentialExceedancesTooltip}
                  text={`${exceedances?.length ?? 0} potential ${pluralize(
                    "exceedance",
                    exceedances?.length ?? 0
                  )} this period`}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "EPA",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "calculatedFederalGeneratorStatus",
                        headerName: "Calculated",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      ...(containerTrackingEnabled
                        ? [{ field: "generationKg", headerName: GENERATION_COLUMN_HEADER, flex: 1 }]
                        : []),
                    ]}
                    rows={(exceedances ?? []).map((row) => ({
                      ...row,
                      generationKg: formatGenerationKg(generationByMonth.get(monthKey(row.month))),
                    }))}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {/* Below threshold - Expandable */}
              {belowThreshold && belowThreshold?.length > 0 && (
                <ExpandableInfoContainerCombined
                  disabled={!belowThreshold?.length}
                  label="Below Threshold"
                  infoTooltip={belowThresholdTooltip}
                  text={`${belowThreshold?.length ?? 0} ${pluralize(
                    "month",
                    belowThreshold?.length ?? 0
                  )} this period`}
                >
                  <GreySimpleGrid
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "EPA",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "calculatedFederalGeneratorStatus",
                        headerName: "Calculated",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      ...(containerTrackingEnabled
                        ? [{ field: "generationKg", headerName: GENERATION_COLUMN_HEADER, flex: 1 }]
                        : []),
                    ]}
                    getRowId={(row) => row.month}
                    rows={(belowThreshold ?? []).map((row) => ({
                      ...row,
                      generationKg: formatGenerationKg(generationByMonth.get(monthKey(row.month))),
                    }))}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {/* State-axis exceedances — calculated state vs reported state,
                  independent of federal. Only surfaced for states that don't
                  follow the federal generator category (e.g. CA). Computed
                  on-read like the federal sections (ENG-16037). */}
              {showState && stateExceedances && stateExceedances.length > 0 && (
                <ExpandableInfoContainerCombined
                  showWarningChip
                  label={`${facilityState} State Potential Exceedances`}
                  infoTooltip={stateExceedancesTooltip}
                  text={`${stateExceedances.length} potential ${pluralize(
                    "exceedance",
                    stateExceedances.length
                  )} this period`}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedStateGeneratorStatus",
                        headerName: "EPA",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "calculatedStateGeneratorStatus",
                        headerName: "Calculated",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    rows={stateExceedances}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {showState && stateBelowThreshold && stateBelowThreshold.length > 0 && (
                <ExpandableInfoContainerCombined
                  label={`${facilityState} State Below Threshold`}
                  infoTooltip={stateBelowThresholdTooltip}
                  text={`${stateBelowThreshold.length} ${pluralize(
                    "month",
                    stateBelowThreshold.length
                  )} this period`}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedStateGeneratorStatus",
                        headerName: "EPA",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "calculatedStateGeneratorStatus",
                        headerName: "Calculated",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    rows={stateBelowThreshold}
                  />
                </ExpandableInfoContainerCombined>
              )}
            </>
          )}
        </Stack>
      </DashboardTile.ContentArea>
    </>
  );
};

function GreySimpleGrid<T extends GridValidRowModel>(props: DataGridProps<T>) {
  const theme = useTheme();
  return (
    <DataGrid
      sx={{
        border: "0px",
        backgroundColor: "transparent",
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: theme.palette.grey50.main,
          p: 1,
        },
        "& .MuiDataGrid-cell": {
          px: 1,
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
      }}
      disableColumnMenu
      disableColumnReorder
      disableColumnSorting
      disableColumnResize
      {...props}
    />
  );
}
